import { type AxiosResponse } from 'axios';

import api, { armorAPI } from '~/services/api';
import { toJSON as assetToJSON, type Asset, type JSONAsset } from '~/types/Asset';
import { type JSONCompany } from '~/types/Company';
import { type JSONIDVerification } from '~/types/IDVerification';
import { type JSONUser } from '~/types/User';
import { getCookie } from '~/utils/cookies';
import { decamelizeKeys } from '~/utils/humps';

type ErrorWithMessages = {
  response: {
    data: {
      errors: string[];
    };
  };
};

const isErrorWithMessages = (error: unknown): error is ErrorWithMessages =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  Array.isArray((error as Maybe<ErrorWithMessages>)?.response?.data?.errors);

const getCurrentUser = () =>
  api.request<JSONUser>({
    method: 'GET',
    url: 'v1/ui/account',
  });

const updateCompany = (payload: JSONCompany) =>
  armorAPI.request<JSONCompany>({
    data: payload,
    headers: {},
    method: 'PUT',
    url: '/setting/company',
  });

const fetchCompany = () =>
  armorAPI.request<JSONCompany>({
    method: 'GET',
    url: '/setting/company/',
  });

const fetchAssets = () =>
  api.request<{ results: JSONAsset[] }>({
    method: 'GET',
    url: '/ui/assets',
  });

const uploadAssetDraft = (assetObjectId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.request({
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    method: 'POST',
    url: `/ui/assets/${assetObjectId}/draft_image`,
  });
};

const publishAssetDraft = (assetObjectId: string) =>
  api.request({
    method: 'POST',
    url: `/ui/assets/${assetObjectId}/draft_image/publish`,
  });

const clearAssetDraft = (assetObjectId: string) =>
  api.request({
    method: 'POST',
    url: `/ui/assets/${assetObjectId}/draft_image/clear`,
  });

const revertAssetDraft = (assetObjectId: string) =>
  api.request({
    method: 'DELETE',
    url: `/ui/assets/${assetObjectId}/draft_image`,
  });

const uploadAndPublishAsset = (assetType: string, file: File) => {
  const formData = new FormData();
  formData.append('asset_type', assetType);
  formData.append('rank', '1');
  formData.append('file', file);
  return api.request({
    data: formData,
    method: 'POST',
    url: '/ui/assets/upload_and_publish',
  });
};

const updateAsset = (asset: Asset) =>
  api.request<JSONAsset>({
    data: assetToJSON(asset),
    method: 'PUT',
    url: `/ui/assets/${asset.objectId}`,
  });

const createAsset = (asset: OptionalObjectId<Asset>) =>
  api.request<JSONAsset>({
    data: assetToJSON(asset),
    method: 'POST',
    url: '/ui/assets',
  });

const createVerificationIntent = (returnUrl: string) =>
  armorAPI.request<{ redirect_url: string }>({
    data: {
      return_url: returnUrl,
    },
    method: 'POST',
    url: '/id_verification',
  });

const getVerificationStatus = () =>
  armorAPI.request<JSONIDVerification>({
    method: 'GET',
    url: '/id_verification',
  });

type FederatedAuth = {
  auth_code: string;
  provider: string;
  state: string;
};

type PasswordAuth = {
  password: string;
};

type UserDataAuth = FederatedAuth | PasswordAuth;

type UpdateUserDataArgs = {
  email: string;
  name: string;
} & UserDataAuth;

export type UpdatePasswordData = {
  newPassword: string;
  newPasswordConfirmation: string;
} & UserDataAuth;

type UpdateUserResponse = {
  email: string;
  first_name: string;
  last_name: string;
};

function updateUser(data: UpdatePasswordData): Promise<AxiosResponse<UpdateUserResponse>>;
function updateUser(data: UpdateUserDataArgs): Promise<AxiosResponse<UpdateUserResponse>>;
function updateUser(data: Record<string, unknown>): Promise<AxiosResponse<UpdateUserResponse>> {
  return api.request({
    data: {
      ...decamelizeKeys(data),
      is_impersonate: getCookie('is_impersonate') || '',
    },
    method: 'PUT',
    url: 'ui/user/profile',
    withCredentials: true,
  });
}

const addUser = (email?: string) =>
  api.request({
    data: {
      email,
    },
    method: 'POST',
    url: '/ui/account/login_users',
  });

const sendInvite = (id: number | string) =>
  api.request({
    method: 'POST',
    url: `/ui/account/login_users/${id}/send_invitation_email`,
  });

const deleteUser = (id: number | string) =>
  api.request({
    method: 'DELETE',
    url: `/ui/account/login_users/${id}`,
  });

const getLoginUsers = () =>
  api.request({
    method: 'GET',
    url: '/ui/account/login_users?results=200',
  });

export {
  addUser,
  clearAssetDraft,
  createAsset,
  createVerificationIntent,
  deleteUser,
  fetchAssets,
  fetchCompany,
  getCurrentUser,
  getLoginUsers,
  getVerificationStatus,
  isErrorWithMessages,
  publishAssetDraft,
  revertAssetDraft,
  sendInvite,
  updateAsset,
  updateCompany,
  updateUser,
  uploadAndPublishAsset,
  uploadAssetDraft,
};
